import Stack from "@mui/material/Stack";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CasinoSportToggle from "@/components/Core/CasinoSportToggle";
import ColorModeIcon from "@/components/Core/ColorModeIcon";
import Logo from "@/components/Core/Logo";
import SiteSearch from "@/components/Others/SiteSearch";
import UserProfile from "@/components/Profile/UserProfile";
import Wallet from "@/components/Profile/Wallet";
import { useChatRoomGameContext, useDeviceType, useDialog, useIsLoggedIn } from "@/hooks";
import { HeaderContainer } from "@/layout/styles";
import { adoptSessionToken } from "@/lib/session";
import { useReturnAfterLogin } from "@/routes/PrivateRoute";
import { useSelector } from "@/store";
import { getStoredReferralCode, handleError } from "@/utils";
import { storePromoCode } from "@/utils/attribution";
import { usePromoCodeRedemption } from "@/views/Auth/hooks/usePromoCodeRedemption";
import Notification from "@/views/Profile/Notification";

import BetSlipButton from "./BetSlipButton";
import DailySpinButton from "./DailySpinButton";
import { LoginButton } from "./LoginButton";
import Message from "./Message";
import SearchButton from "./SearchButton";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = useIsLoggedIn();
  const { isMobile } = useDeviceType();
  const { openDialog } = useDialog();
  const { open: chatRoomOpen } = useChatRoomGameContext();
  const betSlipOpen = useSelector((s) => s.betSlip.isOpen);
  const sideWidth = chatRoomOpen || betSlipOpen;
  useReturnAfterLogin();
  usePromoCodeRedemption();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const handleLogin = useCallback(() => {
    openDialog("auth", { tab: "login" });
  }, [openDialog]);

  const handleRegister = useCallback(() => {
    openDialog("auth", { tab: "register" });
  }, [openDialog]);

  useEffect(() => {
    const type = queryParams.get("type");
    const deposit = queryParams.has("deposit");
    const login = queryParams.has("login");
    const register = queryParams.has("register");
    const ref = queryParams.get("ref");
    const promo = queryParams.get("promo");
    const sessionToken = !type ? queryParams.get("token") : null;

    if (!type && !deposit && !login && !register && !sessionToken && !promo) return;

    // In-app links never pass through the capture at page load.
    if (promo) storePromoCode(promo);

    switch (type) {
      case "reset-password": {
        const token = queryParams.get("token");
        if (token) {
          openDialog("reset-password", token);
        }
        break;
      }
    }

    if (deposit) {
      openDialog("wallet");
    }
    if (login) {
      openDialog("auth", { tab: "login" });
    }
    if (register) {
      const hasStoredCode = Boolean(getStoredReferralCode());
      openDialog("auth", { tab: "register", code: hasStoredCode ? undefined : ref || undefined });
    }
    if (sessionToken) {
      adoptSessionToken(sessionToken)
        .then((restored) => {
          if (!restored) throw new Error("Session expired");
        })
        .catch((error) => handleError(error, "Failed to restore session"));
    }

    navigate(location.pathname, { replace: true });
  }, [queryParams, openDialog, navigate, location.pathname]);

  return (
    <HeaderContainer sideWidth={sideWidth}>
      <Stack direction="row" alignItems="center" gap={1}>
        {isMobile && <Logo />}
        {!isMobile && <CasinoSportToggle />}
      </Stack>
      <Stack direction="row" alignItems="center" gap={{ xs: 0.75, md: 1.25 }}>
        {isMobile ? (
          <>
            {isLoggedIn ? (
              <>
                <Wallet />
                <UserProfile />
              </>
            ) : (
              <>
                <ColorModeIcon />
                <Message />
                <LoginButton onClick={handleLogin} />
              </>
            )}
          </>
        ) : (
          <>
            {isLoggedIn ? (
              <>
                <Wallet />
                <SearchButton />
                <Notification />
                <BetSlipButton />
                {/* Chat, daily spin and colour mode live in the profile menu. */}
                <UserProfile />
              </>
            ) : (
              <>
                <SearchButton />
                <DailySpinButton />
                <BetSlipButton />
                <ColorModeIcon />
                <Message />
                <LoginButton onClick={handleLogin} onRegister={handleRegister} />
              </>
            )}
          </>
        )}
      </Stack>
      {/* Here and not with the other dialogs: its results are router links. */}
      <SiteSearch />
    </HeaderContainer>
  );
};

export default memo(Header);
